import { useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../auth/AuthContext';
import { UserManagement } from './Admin/UserManagement';
import { RoleManagement } from './Admin/RoleManagement';
import { ProjectManagement } from './Admin/ProjectManagement';
import { PhaseManagement } from './Admin/PhaseManagement';
import { ProjectIntroPages } from './Admin/ProjectIntroPages';
import { FeatureManagement } from './Admin/FeatureManagement';
import { AuditLogViewer } from './Admin/AuditLogViewer';
import { AuditSettings } from './Admin/AuditSettings';
import { GoogleAuthSettings } from './Admin/GoogleAuthSettings';
import { PortalManagement } from './Admin/PortalManagement';
import { ChatbotManagement } from './Admin/ChatbotManagement';

interface AdminTab {
  label: string;
  /** Đường dẫn tuyệt đối (dùng cho cả điều hướng và xác định tab đang chọn). */
  to: string;
}

const TABS: AdminTab[] = [
  { label: 'Người dùng', to: '/admin/users' },
  { label: 'Vai trò', to: '/admin/roles' },
  { label: 'Dự án', to: '/admin/projects' },
  { label: 'Cổng truy cập', to: '/admin/portals' },
  { label: 'Chatbot', to: '/admin/chatbot' },
  { label: 'Nhật ký hệ thống', to: '/admin/audit-logs' },
  { label: 'Cấu hình nhật ký', to: '/admin/audit-settings' },
  { label: 'Đăng nhập Google', to: '/admin/google-auth' },
];

/**
 * Khu vực quản trị hệ thống — chỉ dành cho vai trò Admin.
 * Sở hữu toàn bộ định tuyến con qua <Routes> lồng bên trong (users/roles/
 * projects/.../audit-logs). Được App.tsx gắn vào cả "/admin" và "/admin/*".
 */
export function Admin(): JSX.Element {
  const { roles, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = roles.includes('Admin');

  const currentTab = useMemo(() => {
    const found = TABS.find((tab) => location.pathname.startsWith(tab.to));
    return found ? found.to : false;
  }, [location.pathname]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          Quản trị hệ thống
        </Typography>
        <Alert severity="error">
          Bạn không đủ quyền truy cập khu vực quản trị. Vui lòng liên hệ quản trị viên nếu cần hỗ trợ.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Quản trị hệ thống
      </Typography>

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(_, value: string) => navigate(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TABS.map((tab) => (
            <Tab key={tab.to} label={tab.label} value={tab.to} />
          ))}
        </Tabs>
      </Paper>

      <Routes>
        <Route index element={<Navigate to="users" replace />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="roles" element={<RoleManagement />} />
        <Route path="projects" element={<ProjectManagement />} />
        <Route path="projects/:projectId" element={<PhaseManagement />} />
        <Route path="projects/:projectId/phases/:phaseId" element={<FeatureManagement />} />
        <Route path="projects/:projectId/intro" element={<ProjectIntroPages />} />
        <Route path="portals" element={<PortalManagement />} />
        <Route path="chatbot" element={<ChatbotManagement />} />
        <Route path="audit-logs" element={<AuditLogViewer />} />
        <Route path="audit-settings" element={<AuditSettings />} />
        <Route path="google-auth" element={<GoogleAuthSettings />} />
        <Route path="*" element={<Navigate to="users" replace />} />
      </Routes>
    </Box>
  );
}
